import { useDispatch, useSelector } from 'react-redux'
import { selectOptionToStrikeMap, selectPastOptionExpiriesV2, selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Box, FormControl, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { getSecuritiesFromSecuritiesObj, getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { selectSnapshot } from '../../../../selectors/security'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import { getOptionPastExpiries } from '../../../../services/apis/optionChain'
import { DropDownExpiries, setDropdownPastOptionExpiries } from '../../../../reducer/options.reducer'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../../components/custom'
import { ChartContext } from '../../../../components/chartV2/chart.context'
import { fetchOptionExpiriesV2 } from '../../../../util/options'
import { ChartViewKey, MULTIPANE_FULLSCREEN_MODE_ID } from '../../../../assets/constants'
import { selectMultiPaneFullScreen } from '../../../../selectors/common'
import { findNearestNumber } from '../../../../util/tradepanel'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../../../assets/routes'
import ThinArrowRightIcon from '../../../../assets/icons/ThinArrowRightIcon'
import { selectActiveLayoutId } from '../../../../selectors/home'
import { selectActiveWidget } from '../../../../selectors/widgetDashboard'
import { WidgetTypes } from '../../../../components/widgetDashboard/widgetDashboard.enums'
import { updateAtmOptionChart } from '../../../../reducer/chart.reducer'
import { selectActiveChartId } from '../../../../selectors/chart'
import { selectIsEmbedded } from '../../../../selectors/device.ts'
import { useUser } from '../../../../context/UserContext.tsx'
import { FeatureCategory, FeatureName } from '../../../../components/adminUserRestrictions/Restrictions.constants.ts'
import { Group, setFeatureRestrictionDialog } from '../../../../reducer/user.reducer.ts'
import { FeatureCode } from '../../../../util/RestrictionFeatureCode.ts'
import { callNative } from '../../../../util/nativeBridge'

export type Props = {
    defaultName?: string
}
const OptionsChartExpiryMenu: React.FC<Props> = ({ defaultName }: Props) => {
    const dispatch = useDispatch()
    const setPrimarySecurity = useSetPrimarySecurity()
    const navigate = useNavigate()
    const {user :{ hasAccess }} = useUser()
    const chartContext = useContext(ChartContext)
    const activeChartId = chartContext?.viewKey
    const chartSecurities = chartContext?.chart?.securities
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
    const underlyingTokenFromChart = chartSecurities?.length && chartSecurities[0].exchange != null && chartSecurities[0].underlyingSecurityCode != null
        ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].underlyingSecurityCode)
        : ''
    const underlyingTokenFromWidget = (activeWidget?.settings as { underlyingKey?: string })?.underlyingKey ?? ''
    const underlyingToken = underlyingTokenFromChart || underlyingTokenFromWidget
    const underlyingSnaphot = useSelector(selectSnapshot(underlyingToken))
    const [underlying, setUnderlying] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (underlyingToken) getSecurityAsync(underlyingToken).then(setUnderlying); else setUnderlying(undefined) }, [underlyingToken])
    const key = useMemo(()=>chartSecurities?.length ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].code) : '', [chartSecurities])
    const [selectedChartSecurity, setSelectedChartSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (key) getSecurityAsync(key).then(setSelectedChartSecurity); else setSelectedChartSecurity(undefined) }, [key])
    const pastExpiries = useSelector(selectPastOptionExpiriesV2(underlyingToken))
    const [optionExpiryMenuAnchorEl, setOptionExpiryMenuAnchorEl] = useState(null)
    const [optionExpirySubmenuAnchorEl, setOptionExpirySubmenuAnchorEl] = useState(null)
    const optionsTradesData = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken, false))
    const optionToStrikeMap = useSelector(selectOptionToStrikeMap(underlyingToken))
    const viewKey = useSelector(selectActiveChartId)
    const isEmbedded = useSelector(selectIsEmbedded)
    const hasPastExpiryAccess = hasAccess(FeatureCategory.CHART, '', FeatureName.PAST_EXPIRY)
    
    const fetchOptionsPastExpiries = () => {
        if (!underlying) return
        getOptionPastExpiries({
            exchange: underlying.exchange,
            securityCode: underlying.code
        }).then((response) => {
            dispatch(
                setDropdownPastOptionExpiries({
                    token: underlyingToken,
                    optionExpiries: response
                })
            )
        })
    }

    const expiryLabel = useMemo(() => {
        if (!selectedChartSecurity?.expiry?.name) return ''
        return selectedChartSecurity?.expiry?.name
    }, [selectedChartSecurity])

    const handleOptionExpiryChange = useCallback(
        async (optionExpiry: DropDownExpiries, atmStrike: number) => {
            const chartSecKey = key
            const selectedOptionType = optionToStrikeMap[chartSecKey]
            let strike = selectedOptionType ? selectedOptionType.strike : 0
            let options = optionExpiry.strikes.find((s) => s.strike === strike)
            if (optionExpiry && !options) {
                strike = atmStrike ? atmStrike : optionExpiry.strikes[Math.round((optionExpiry.strikes.length - 1) / 2)].strike
                options = optionExpiry.strikes.find((s) => s.strike === strike)
            }
            const option = selectedOptionType && selectedOptionType.optionType === 'PE' ? options?.putSecurity : options?.callSecurity
            if (option) {
                const newOptionKey = getSecurityKey(option.exchange, option.code)
                const sec = await getSecuritiesFromSecuritiesObj([option])
                if (sec) {
                    if (activeChartId === ChartViewKey.SYMBOL) {
                        navigate(generatePath(ROUTES.symbol, { symbol: getSecurityKey(sec[0].exchange, sec[0].code) }))
                    }
                    if (activeWidget?.widgetType === WidgetTypes.ATM_OPTION_CHART) {
                        const configChartKey = selectedChartSecurity ? getSecurityKey(selectedChartSecurity.exchange, selectedChartSecurity.code) : chartSecKey
                        dispatch(updateAtmOptionChart({ viewKey, config: { key: configChartKey, securityKey: newOptionKey }, createIfAbsent: true }))
                    } else {
                        setPrimarySecurity(newOptionKey, sec, false)
                    }
                }
                AnalyticsManager.logEvent('Options Price Charts Expiry Changed', { selected_value: sec?.[0].code })
            }
            handleOptionExpiryMenuClose()
        },
        [optionToStrikeMap, key, setPrimarySecurity, activeChartId]
    )

    const handleOptionExpiryMenuClick = (event: any) => {
        if (isEmbedded) {
            if (window.isEmbeddedMode) {
                const expiry = selectedChartSecurity?.expiry?.expiry || 0
                const securityKey = key || ''
                const metaData = {
                    type: 'expiry',
                    expiry: expiry,
                    securityKey: securityKey,
                    timestamp: Date.now()
                }
                callNative('onScalperSeriesMetaDataClicked', JSON.stringify(metaData))
            } else {
                callNative('onSeriesMetaDataOptionExpiryClicked')
            }
        } else {
            if (!optionsTradesData?.length && underlying) {
                fetchOptionExpiriesV2(underlying, dispatch)
            }
            if (selectedChartSecurity?.expiry?.expiry && selectedChartSecurity.expiry.expiry < new Date().getTime() && !pastExpiries) {
                fetchOptionsPastExpiries()
            }
            setOptionExpiryMenuAnchorEl(event.currentTarget)
        }
    }

    const handleOptionExpiryMenuClose = () => {
        setOptionExpiryMenuAnchorEl(null)
        setOptionExpirySubmenuAnchorEl(null)
    }

    const openOptionExpirySubmenu = (event: any) => {
        if(!hasAccess(FeatureCategory.CHART, '', FeatureName.PAST_EXPIRY)){
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Past expiries",
                group: Group.CHART,
                featureRestrictionCode:FeatureCode.PAST_EXPIRY
            }))
            return
        }
        if (!pastExpiries) {
            fetchOptionsPastExpiries()
        }
        setOptionExpirySubmenuAnchorEl(event.currentTarget)
    }
    const container = useSelector(selectMultiPaneFullScreen)

    return (
        <FormControl>
            <NonInteractiveTooltip placement={'top'} title="Change Expiry" arrow>
                <ExtraSmallButton
                    size="small"
                    variant="text"
                    onClick={handleOptionExpiryMenuClick}
                    color="inherit"
                    sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', fontSize: '14px', minWidth: 0 }}
                >
                    {defaultName && !selectedChartSecurity?.derived ? `${expiryLabel}(${defaultName})` : expiryLabel}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
            {Boolean(optionExpiryMenuAnchorEl) && !isEmbedded && (
                <Menu
                    MenuListProps={{
                        onMouseLeave: () => {
                            handleOptionExpiryMenuClose()
                        }
                    }}
                    container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                    id="basic-menu"
                    anchorEl={optionExpiryMenuAnchorEl}
                    open={Boolean(optionExpiryMenuAnchorEl)}
                    autoFocus={true}
                    onClose={handleOptionExpiryMenuClose}
                >
                    {optionsTradesData?.map((o, i) => {
                        const atmStrike = findNearestNumber(o.strikesArray, underlyingSnaphot?.lastTradedPrice)
                        if (activeWidget?.widgetType === WidgetTypes.ATM_OPTION_CHART && !o?.derived) {
                            return null
                        }
                        return (
                            <MenuItem key={i} value={o.expiry.name} selected={o.expiry.name === expiryLabel} onClick={() => handleOptionExpiryChange(o as any, atmStrike ?? 0)}>
                                {o.expiry.name}
                            </MenuItem>
                        )
                    })}
                    {activeWidget?.widgetType !== WidgetTypes.ATM_OPTION_CHART && (
                        <Box component="span" sx={{ display: 'contents' }}>
                            <Box>
                                <MenuItem
                                onClick={openOptionExpirySubmenu}
                                selected={pastExpiries?.some(ex => ex.expiry.name === expiryLabel)}
                                >
                                    <Box
                                        sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%'
                                        }}
                                    >
                                        <ListItemText sx={{ opacity: 1}}>Past Expiry</ListItemText>
                                        <ListItemIcon sx={{ minWidth: 24 }}>
                                            <ThinArrowRightIcon fontSize="small" />
                                        </ListItemIcon>
                                    </Box>
                                </MenuItem>
                            </Box>
                            <Menu
                                MenuListProps={{
                                    onMouseLeave: () => {
                                        setOptionExpirySubmenuAnchorEl(null)
                                    }
                                }}
                                id="submenu"
                                anchorEl={optionExpirySubmenuAnchorEl}
                                open={Boolean(optionExpirySubmenuAnchorEl)}
                                onClose={handleOptionExpiryMenuClose}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left'
                                }}
                            >
                                {pastExpiries?.map((drowDownExpiry: DropDownExpiries) => (
                                    <MenuItem
                                        key={drowDownExpiry.expiry.name}
                                        selected={drowDownExpiry.expiry.name === expiryLabel}
                                        value={drowDownExpiry.expiry.name}
                                        onClick={() => {
                                            handleOptionExpiryChange(drowDownExpiry, 0)
                                            handleOptionExpiryMenuClose()
                                        }}
                                    >
                                        {drowDownExpiry.expiry.name}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    )}
                </Menu>
            )}
        </FormControl>
    )
}

// expiry: Expiry
//     strikes: DropDownStrike[]
//     strikesArray: number[]
//     derived: boolean

export default memo(OptionsChartExpiryMenu)
